/**
 * AffirmationFolderUploader — Admin-only inline folder uploader displayed on
 * each affirmation theme card. Lets the admin select an entire folder (or
 * multiple audio files at once) whose filenames start with the affirmation
 * index ("0_xxx.mp3", "00.mp3", "23-affirmation.mp3"). Each file is sent to
 * the matching ludovic slot of the theme.
 *
 * Uses webkitdirectory + multiple so the user can pick a folder on desktop
 * (or multi-select files on mobile, which doesn't support directory picking).
 */
import React, { useRef, useState, useEffect } from 'react';
import { Folder, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const parseIndex = (filename) => {
  // Strip any folder prefix (when using webkitdirectory the input keeps the
  // file's bare name but we strip just in case).
  const base = filename.split('/').pop();
  // Accept: "00_x.mp3", "0.mp3", "1-x.mp3", "12.mp3", "23 my voice.mp3"
  const m = base.match(/^(\d{1,3})[._\-\s]/);
  if (m) return parseInt(m[1], 10);
  const m2 = base.match(/^(\d{1,3})\.[a-z0-9]+$/i);
  if (m2) return parseInt(m2[1], 10);
  return null;
};

export default function AffirmationFolderUploader({ theme, onDone }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Set the non-standard "webkitdirectory" attribute via ref so React doesn't
  // warn about an unknown DOM property and so it works on Chromium browsers.
  useEffect(() => {
    if (inputRef.current) {
      try {
        inputRef.current.setAttribute('webkitdirectory', '');
        inputRef.current.setAttribute('directory', '');
        inputRef.current.setAttribute('mozdirectory', '');
      } catch {
        /* attribute setting failed silently */
      }
    }
  }, []);

  if (!theme) return null;
  const maxIndex = (theme.affirmations?.length || 0) - 1;

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const valid = [];
    const skipped = [];
    Array.from(files).forEach((f) => {
      // Only keep audio files
      if (!/\.(mp3|m4a|wav|ogg|webm|aac)$/i.test(f.name)) return;
      const idx = parseIndex(f.name);
      if (idx === null || isNaN(idx) || idx < 0 || idx > maxIndex) {
        skipped.push(f.name);
      } else {
        valid.push({ idx, file: f });
      }
    });
    if (valid.length === 0) {
      toast.error(
        'Aucun fichier audio reconnu. Nommez vos fichiers "0.mp3", "01_xxx.mp3", "23-affirmation.mp3"…'
      );
      return;
    }
    if (skipped.length > 0) toast.info(`${skipped.length} fichier(s) ignoré(s)`);
    valid.sort((a, b) => a.idx - b.idx);

    setBusy(true);
    setProgress({ done: 0, total: valid.length });
    let ok = 0;
    let err = 0;
    for (let i = 0; i < valid.length; i++) {
      const { idx, file } = valid[i];
      setProgress({ done: i, total: valid.length });
      try {
        const fd = new FormData();
        fd.append('file', file, file.name);
        await axios.post(
          `${API}/api/affirmation-clips/${theme.id}/${idx}/ludovic/upload`,
          fd
        );
        ok++;
      } catch (e) {
        err++;
        console.error(`[${theme.id}] upload failed idx=${idx}`, e);
      }
    }
    setProgress({ done: valid.length, total: valid.length });
    setBusy(false);
    if (err === 0) toast.success(`${ok} clip(s) chargé(s) pour « ${theme.label} » ✓`);
    else toast.warning(`${theme.label} : ${ok} ok · ${err} échec(s)`);
    onDone && onDone();
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      className="mt-2 flex flex-col gap-1"
      data-testid={`folder-uploader-${theme.id}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm,.aac"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
        data-testid={`folder-uploader-input-${theme.id}`}
      />
      <button
        type="button"
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          inputRef.current?.click();
        }}
        className="w-full inline-flex items-center justify-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded-full border border-amber-400/40 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 disabled:opacity-50 transition"
        title="Charger un dossier d'audios Ludovic pour ce thème"
        data-testid={`folder-uploader-btn-${theme.id}`}
      >
        {busy ? (
          <>
            <Loader2 size={10} className="animate-spin" />
            {progress.done}/{progress.total}
          </>
        ) : (
          <>
            <Folder size={10} />
            Charger dossier
          </>
        )}
      </button>
      {busy && progress.total > 0 && (
        <div className="h-0.5 w-full bg-black/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-400 transition-all"
            style={{ width: `${(progress.done / progress.total) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}
